import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLocalizations } from '@/hooks/useLocalizations';
import type { Notification, NotificationData } from '@/types/analytics';

export type { Notification, NotificationData };

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { lCommon } = useLocalizations();
  const navigate = useNavigate();

  // Store navigate in ref to avoid effect re-runs when navigate identity changes
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  // Store lCommon in ref to avoid effect re-runs
  const lCommonRef = useRef(lCommon);
  useEffect(() => { lCommonRef.current = lCommon; }, [lCommon]);

  // Fetch notifications
  const { data: notifications, isLoading } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as Notification[];
    },
    enabled: !!user?.id,
  });

  // Get unread count
  const unreadCount = notifications?.filter((n) => !n.is_read).length || 0;

  // Real-time subscription
  useEffect(() => {
    if (!user?.id) return;

    // A channel name unique to this mount: reusing one fixed name makes the
    // Supabase client throw when a second window or remount attaches listeners
    // to a channel that is already subscribed, which crashes the page.
    const channel = supabase
      .channel(`user-notifications-${user.id}-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          
          // Show toast for new notification
          toast(newNotification.title, {
            description: newNotification.message,
            action: newNotification.link
              ? {
                  label: lCommonRef.current('actions.view', { defaultValue: 'View' }),
                  onClick: () => {
                    const link = newNotification.link!;
                    // Internal links - use React Router navigate
                    if (link.startsWith('/') && !link.startsWith('//')) {
                      navigateRef.current(link);
                    } else {
                      window.location.href = link;
                    }
                  },
                }
              : undefined,
          });

          // Invalidate query to refetch
          queryClient.invalidateQueries({
            queryKey: ['notifications', user.id],
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('user_notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications', user?.id],
      });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) return;

      const { error } = await supabase
        .from('user_notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notifications', user?.id],
      });
      toast.success(lCommon('toast.notifications_marked_read.success', { defaultValue: 'Notifications marked as read' }));
    },
  });

  return {
    notifications: notifications || [],
    unreadCount,
    isLoading,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
  };
}